import { useCallback, useEffect, useRef } from 'react';

import { RollsPlayer } from '@/entities/rolls';
import { twa } from '@/shared/lib/telegram/twa';

import { WheelSegment } from './useWheelSegments';

export function usePointerTracking(
  segments: WheelSegment[],
  isSpinning: boolean,
  onCurrentPlayer?: (player: RollsPlayer | null) => void,
) {
  const lastPlayerIdRef = useRef<string | null>(null);
  const segmentsRef = useRef(segments);
  segmentsRef.current = segments;

  // Reset when not spinning
  useEffect(() => {
    if (!isSpinning) {
      lastPlayerIdRef.current = null;
      onCurrentPlayer?.(null);
    }
  }, [isSpinning]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDiscUpdate = useCallback(
    (latest: Record<string, number>) => {
      if (!onCurrentPlayer || segmentsRef.current.length === 0) return;

      const r = latest.rotate ?? 0;
      // The pointer is at top (12 o'clock). SVG is rotated -90° so logical 0° = top.
      // When disc rotates by R clockwise, the logical angle under the pointer:
      const pointerAngle = (((360 - (r % 360)) % 360) + 360) % 360;

      for (const seg of segmentsRef.current) {
        const segEnd = seg.startAngle + seg.angle;
        if (pointerAngle >= seg.startAngle && pointerAngle < segEnd) {
          if (seg.id !== lastPlayerIdRef.current) {
            lastPlayerIdRef.current = seg.id;
            twa.hapticFeedback.selectionChanged();
            onCurrentPlayer(seg);
          }
          return;
        }
      }
      // Fallback (angle ≈ 360 wraps to 0 → first segment)
      const first = segmentsRef.current[0];
      if (first && first.id !== lastPlayerIdRef.current) {
        lastPlayerIdRef.current = first.id;
        onCurrentPlayer(first);
      }
    },
    [onCurrentPlayer],
  );

  return handleDiscUpdate;
}
